import { Injectable } from "@angular/core";
import {
  AlarmTypeKey,
  NIGHTOWL_SUPERVISION_RESUMED,
  NIGHTOWL_SUPERVISION_PAUSED,
  NIGHTOWL_DIGITAL_SUPERVISION,
} from "app/models/camera.model";

export interface IActivationTranslation {
  group: number;
  node: number;
  deviceType: string;
  name: string;
}

// Technical alarms are excluded by activation group.
export const TECHNICAL_ALARM_GROUPS = new Set<number>([4, 81, 160]);

// Additional specific alarm types excluded by deviceType + group + node.
export const EXCLUDED_ALARM_TYPES: AlarmTypeKey[] = [
  { ...NIGHTOWL_SUPERVISION_RESUMED },
  { ...NIGHTOWL_SUPERVISION_PAUSED },
  { ...NIGHTOWL_DIGITAL_SUPERVISION }
];

@Injectable({ providedIn: 'root' })
export class CameraAlarmTypeService {

  getActivationsFromStorage(): IActivationTranslation[] {
    try {
      const parsed = JSON.parse(localStorage.getItem("act") || "[]");
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && typeof item === "object")
        : [];
    } catch {
      return [];
    }
  }

  getAlarmTypeKeyString(key: AlarmTypeKey): string {
    return `${key.devicePrefixClass}|${key.alarmGroup}|${key.alarmNode}`;
  }

  getDeviceTypeName(deviceType: string): string {
    try {
      const devTypes = JSON.parse(localStorage.getItem('devType') || '[]');
      if (Array.isArray(devTypes)) {
        const found = devTypes.find((d: any) => d.deviceType === deviceType);
        if (found && found.deviceTypeName) {
          return found.deviceTypeName;
        }
      }
    } catch {
      // intentionally ignored
    }
    // fallback for known types
    if (deviceType === 'Camera') return 'Digital Supervision';
    if (deviceType === 'Nightowl' || deviceType === '34-4') return 'Night Owl';
    return deviceType;
  }

  isTechnicalAlarm(activation: IActivationTranslation): boolean {
    if (TECHNICAL_ALARM_GROUPS.has(activation.group)) {
      return true;
    }
    return EXCLUDED_ALARM_TYPES.some(
      (e) => e.devicePrefixClass === activation.deviceType && e.alarmGroup === activation.group && e.alarmNode === activation.node
    );
  }

  /**
   * Resolves an array of AlarmTypeKey to grouped display names.
   * Returns null if eventsToTrig is undefined/null (meaning all activations are enabled).
   */
  getAlarmTypeLabels(eventsToTrig: AlarmTypeKey[] | undefined): { groupName: string; labels: string[] }[] | null {
    if (!eventsToTrig || eventsToTrig.length === 0) {
      return null; // null = "all"
    }
    const activations = this.getActivationsFromStorage();
    const grouped = new Map<string, string[]>();

    eventsToTrig.forEach((key) => {
      const match = activations.find(
        (a) =>
          a.deviceType === key.devicePrefixClass &&
          Number(a.group) === Number(key.alarmGroup) &&
          Number(a.node) === Number(key.alarmNode)
      );
      const label = match?.name || this.getAlarmTypeKeyString(key);
      const groupName = this.getDeviceTypeName(key.devicePrefixClass);
      if (!grouped.has(groupName)) {
        grouped.set(groupName, []);
      }
      grouped.get(groupName).push(label);
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([groupName, labels]) => ({ groupName, labels: labels.sort() }));
  }
}
